/**
 * La carte : MapLibre + PMTiles, le tracé, les gares.
 *  - clic sur le tracé → gare au PK le plus proche
 *  - clic sur une gare fantôme → on la remet
 *  - glisser une gare → elle suit le tracé, le chiffre bouge pendant le glissement
 */
import {
  Map as MapLibreMap,
  NavigationControl,
  ScaleControl,
  addProtocol,
  setWorkerUrl,
  type GeoJSONSource,
  type LngLat,
  type MapLayerMouseEvent,
  type MapLayerTouchEvent,
  type MapMouseEvent,
  type MapTouchEvent,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
// MapLibre 6 charge son worker via `new URL("./maplibre-gl-worker.mjs", import.meta.url)`,
// que ni le serveur de dev ni le bundle ne savent servir : on le fait empaqueter par Vite.
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { Protocol } from "pmtiles";
import { useEffect, useRef } from "react";
import { nearestIndex } from "../model/snap";
import { useApp } from "../state/store";
import { ghostFeatures, routeFeatures, stopFeatures } from "./geojson";
import { GHOSTS_SOURCE, HOVER_SOURCE, ROUTE_SOURCE, STOPS_SOURCE, buildStyle } from "./style";

const BASE = import.meta.env.BASE_URL;
const SNAP_TOLERANCE_M = 400;

let protocolRegistered = false;
setWorkerUrl(workerUrl);

export function MapView() {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const ready = useRef(false);

  const route = useApp((s) => s.route);
  const result = useApp((s) => s.result);
  const stops = useApp((s) => s.scenario.stops);
  const names = useApp((s) => s.scenario.names);
  const selected = useApp((s) => s.selected);
  const hoverIdx = useApp((s) => s.hoverIdx);

  // --- création de la carte -------------------------------------------------
  useEffect(() => {
    if (!container.current || mapRef.current) return;
    if (!protocolRegistered) {
      const protocol = new Protocol();
      addProtocol("pmtiles", protocol.tile);
      protocolRegistered = true;
    }
    const map = new MapLibreMap({
      container: container.current,
      style: buildStyle(BASE),
      center: [55.53, -21.12],
      zoom: 9.3,
      minZoom: 8,
      maxZoom: 16,
      maxBounds: [
        [54.6, -21.9],
        [56.4, -20.4],
      ],
      attributionControl: { compact: true },
      dragRotate: false,
      pitchWithRotate: false,
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new ScaleControl({ maxWidth: 120 }), "bottom-left");
    mapRef.current = map;
    map.on("error", (e) => console.error("[carte]", e.error?.message ?? e));
    if (import.meta.env.DEV) {
      map.once("load", () => console.log("[carte] load", map.getStyle().layers.length, "couches"));
      map.once("idle", () => console.log("[carte] idle, tuiles chargées :", map.areTilesLoaded()));
    }
    if (import.meta.env.DEV) (window as unknown as { __map: MapLibreMap }).__map = map;

    map.on("load", () => {
      ready.current = true;
      refresh();
      bindInteractions(map);
    });
    return () => {
      map.remove();
      mapRef.current = null;
      ready.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- mise à jour des sources ----------------------------------------------
  const refresh = () => {
    const map = mapRef.current;
    const st = useApp.getState();
    if (!map || !ready.current || !st.route) return;
    (map.getSource(ROUTE_SOURCE) as GeoJSONSource).setData(routeFeatures(st.route, st.result));
    (map.getSource(STOPS_SOURCE) as GeoJSONSource).setData(
      stopFeatures(st.route, st.scenario.stops, st.selected, st.stationName),
    );
    (map.getSource(GHOSTS_SOURCE) as GeoJSONSource).setData(ghostFeatures(st.route, st.scenario.stops));
    (map.getSource(HOVER_SOURCE) as GeoJSONSource).setData({
      type: "FeatureCollection",
      features:
        st.hoverIdx === null
          ? []
          : [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: st.route.coords[st.hoverIdx] } }],
    });
  };
  useEffect(refresh, [route, result, stops, names, selected, hoverIdx]);

  // --- interactions ----------------------------------------------------------
  const bindInteractions = (map: MapLibreMap) => {
    const snap = (lngLat: LngLat) => {
      const r = useApp.getState().route;
      if (!r) return null;
      const s = nearestIndex(r.coords, lngLat.lng, lngLat.lat);
      return s.dist_m <= SNAP_TOLERANCE_M ? s.idx : null;
    };

    map.on("click", (e: MapMouseEvent) => {
      const st = useApp.getState();
      const hits = map.queryRenderedFeatures(e.point, { layers: ["stops", "ghosts", "route-hit"] });
      const stop = hits.find((f) => f.layer.id === "stops");
      if (stop) {
        st.select(stop.properties.idx as number);
        return;
      }
      const ghost = hits.find((f) => f.layer.id === "ghosts");
      if (ghost) {
        st.addStop(ghost.properties.idx as number);
        return;
      }
      if (hits.some((f) => f.layer.id === "route-hit")) {
        const idx = snap(e.lngLat);
        if (idx !== null) st.addStop(idx);
        return;
      }
      st.select(null);
    });

    for (const layer of ["stops", "ghosts", "route-hit"]) {
      map.on("mouseenter", layer, () => (map.getCanvas().style.cursor = layer === "stops" ? "grab" : "pointer"));
      map.on("mouseleave", layer, () => (map.getCanvas().style.cursor = ""));
    }

    // glisser-déposer d'une gare le long du tracé
    let dragIdx: number | null = null;
    const onMove = (e: MapMouseEvent | MapTouchEvent) => {
      if (dragIdx === null) return;
      const idx = snap(e.lngLat);
      if (idx !== null && idx !== dragIdx) {
        useApp.getState().moveStop(dragIdx, idx);
        if (!useApp.getState().scenario.stops.includes(dragIdx)) dragIdx = idx;
      }
    };
    const onUp = () => {
      if (dragIdx === null) return;
      map.off("mousemove", onMove);
      map.off("touchmove", onMove);
      map.dragPan.enable();
      map.getCanvas().style.cursor = "";
      useApp.getState().setDragging(null);
      dragIdx = null;
    };
    const onDown = (e: MapLayerMouseEvent | MapLayerTouchEvent) => {
      const f = e.features?.[0];
      if (!f) return;
      if ("touches" in e.originalEvent && e.originalEvent.touches.length > 1) return;
      e.preventDefault();
      dragIdx = f.properties.idx as number;
      useApp.getState().select(dragIdx);
      useApp.getState().setDragging(dragIdx);
      map.dragPan.disable();
      map.getCanvas().style.cursor = "grabbing";
      map.on("mousemove", onMove);
      map.on("touchmove", onMove);
      map.once("mouseup", onUp);
      map.once("touchend", onUp);
      map.once("touchcancel", onUp);
    };
    map.on("mousedown", "stops", onDown);
    map.on("touchstart", "stops", onDown);
  };

  return <div ref={container} className="carte" role="application" aria-label="Carte du tracé" />;
}
